import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../../lib/i18n";

// Animated count-up triggered on scroll-into-view.
const useCountUp = (target, duration = 1600) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.floor(eased * target));
              if (t < 1) requestAnimationFrame(tick);
              else setValue(target);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return [ref, value];
};

const StatCard = ({ value, suffix, label, delay }) => {
  const [ref, n] = useCountUp(value);
  return (
    <div
      ref={ref}
      className="
        group relative rounded-3xl
        bg-gradient-to-br from-[#0E1A2A] via-[#0A1322] to-[#05080E]
        border border-white/[0.07]
        p-8 md:p-10
        transition-all duration-300
        hover:border-brand-orange/40 hover:-translate-y-1
        hover:shadow-[0_24px_60px_-12px_rgba(255,106,0,0.18)]
      "
      style={{ animationDelay: `${delay}ms` }}
    >
      <span
        aria-hidden
        className="absolute -top-20 -right-16 w-44 h-44 rounded-full bg-brand-orange/10 blur-3xl opacity-50 group-hover:opacity-100 transition-opacity"
      />
      <div className="relative z-10">
        <div className="font-display font-bold text-white text-[56px] md:text-[64px] tracking-tight leading-none">
          {n}
          <span className="text-brand-orange">{suffix}</span>
        </div>
        <div className="mt-3 text-white/60 text-[15px] font-medium">{label}</div>
      </div>
    </div>
  );
};

export const Stats = () => {
  const { t } = useI18n();
  const s = t.stats;
  return (
    <section
      data-testid="stats"
      className="relative py-24 md:py-28 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-3xl mb-12">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-12 h-px bg-brand-orange" />
          <span className="label-mono">{s.kicker}</span>
        </div>
        <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-white">
          {s.title}
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {s.items.map((it, i) => (
          <StatCard key={i} value={it.value} suffix={it.suffix} label={it.label} delay={i * 80} />
        ))}
      </div>
    </section>
  );
};
